import { ReactElement, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { useNavigate } from 'react-router-dom';
import { RiskAssessmentService } from 'services/RiskAssessmentService';

interface IStatusCount {
  total: number;
  completed: number;
  inProgress: number;
  pending: number;
}

interface InventoryRowProps {
  label: string;
  value: string | number;
  subText?: string;
}

const InventoryRow = ({
  label,
  value,
  subText,
}: InventoryRowProps): React.ReactElement => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    className="inventory-row"
    sx={{ py: 1 }}
  >
    <Stack
      direction="row"
      alignItems="center"
      gap={0.75}
      className="inventory-row-label-group"
    >
      <Typography
        className="inventory-row-label"
        sx={{ color: '#6b7280', fontSize: '14px' }}
      >
        {label}
      </Typography>
    </Stack>
    <Stack direction="column" alignItems="flex-end">
      <Typography
        className="inventory-row-value"
        sx={{ fontWeight: 600, color: '#052921', fontSize: '16px' }}
      >
        {value}
      </Typography>
      {subText && (
        <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
          {subText}
        </Typography>
      )}
    </Stack>
  </Stack>
);

const TrainingManagement = (): ReactElement => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [assessmentStats, setAssessmentStats] = useState<IStatusCount>({
    total: 0,
    completed: 0,
    inProgress: 0,
    pending: 0,
  });

  const loadData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const statsResponse =
        await RiskAssessmentService.getExternalAssessmentStats();
      const { data: statsData } = statsResponse.data;
      setAssessmentStats({
        total: statsData?.total || 0,
        completed: statsData?.completed || 0,
        inProgress: statsData?.inProgress || 0,
        pending: statsData?.pending || 0,
      });
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const { total, completed, inProgress, pending } = assessmentStats;

  if (isLoading) {
    return (
      <Box className="empty-content">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card variant="outlined">
      <CardHeader
        title="External Assessments"
        subheader="Track risk assessments sent to internal employees and external vendors."
        action={
          <IconButton onClick={() => navigate('/external-assessments')}>
            <RemoveRedEyeIcon />
          </IconButton>
        }
        sx={{
          '& .MuiCardHeader-title': {
            fontSize: '18px',
            fontWeight: 600,
            color: '#052921',
          },
          '& .MuiCardHeader-subheader': {
            fontSize: '14px',
            color: '#6b7280',
          },
        }}
      />

      <CardContent>
        <Box sx={{ mt: 3 }}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={1}>
                <InventoryRow label="Total Send" value={total} />
                <InventoryRow label="Pending" value={pending} />
                <InventoryRow label="In Progress" value={inProgress} />
                <InventoryRow label="Completed" value={completed} />
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TrainingManagement;
